/**
 * The font families an email may use, and how to recognise one that is already set.
 *
 * ### Why a list and not a font picker
 *
 * An email is not a web page: there is no `@font-face` to rely on, no webfont in
 * Outlook, and a family the recipient's client does not have is silently swapped for
 * one it does. So the only fonts worth offering are the ones installed everywhere —
 * the classic web-safe set — each shipped **with its fallbacks**, which is what keeps
 * the swap a near-identical metric rather than the client's default serif.
 *
 * Same reasoning as `TEXT_SIZES` in `RichTextField`: a free text box here is how an
 * email ends up asking for a font a third of its recipients cannot render.
 *
 * ### It must agree with the gate
 *
 * `richTextValue.ts` admits `font-family` in a `<span style>`, and `InlineTextStyle`
 * holds it. A stack must therefore never contain a `;` — the gate splits a style
 * attribute on it — which the test asserts rather than trusting.
 */

export interface EmailFont {
  /** What the dropdown shows: the family a user recognises, not the whole stack. */
  label: string;
  /** What lands in the email: the family plus the fallbacks clients do have. */
  stack: string;
}

/**
 * Ordered sans-first, because email copy is: the serifs and the monospace are the
 * deliberate choices and sit below them.
 */
export const EMAIL_SAFE_FONTS: EmailFont[] = [
  { label: 'Arial', stack: 'Arial, Helvetica, sans-serif' },
  { label: 'Helvetica', stack: 'Helvetica, Arial, sans-serif' },
  { label: 'Verdana', stack: 'Verdana, Geneva, sans-serif' },
  { label: 'Tahoma', stack: 'Tahoma, Verdana, sans-serif' },
  { label: 'Trebuchet MS', stack: "'Trebuchet MS', Helvetica, sans-serif" },
  { label: 'Georgia', stack: "Georgia, 'Times New Roman', serif" },
  { label: 'Times New Roman', stack: "'Times New Roman', Times, serif" },
  { label: 'Courier New', stack: "'Courier New', Courier, monospace" },
];

/**
 * A stack as its identity, so two spellings of the same one compare equal.
 *
 * The editor never sees the string it wrote back: the value round-trips through the
 * DOM, which re-quotes family names with double quotes and re-spaces the commas — so
 * `'Trebuchet MS', Helvetica` returns as `"Trebuchet MS", Helvetica`. Comparing raw
 * strings would leave the dropdown reading *Default* over text that plainly has a
 * font, so matching is done on this form.
 */
export function normalizeFontStack(value: string): string {
  return value
    .split(',')
    .map((family) => family.trim().replace(/^['"]|['"]$/g, '').toLowerCase())
    .filter(Boolean)
    .join(', ');
}

/**
 * The listed stack this value means, or null when it means something else.
 *
 * Null for both "nothing set" and "a family not on the list" — a partner's own
 * `font-family` passes the gate and must not be shown as one of ours. The caller
 * decides how to display it; what matters here is that it is not claimed.
 */
export function matchFontStack(value: string | null | undefined): string | null {
  if (!value) return null;
  const key = normalizeFontStack(value);
  return (
    EMAIL_SAFE_FONTS.find((font) => normalizeFontStack(font.stack) === key)
      ?.stack ?? null
  );
}
